import { DirectSalesMediaType, DirectSalesPricingMode } from 'components/common/types/DirectSalesCampaign.types';
import {
  PUBLISHER_DEFAULT_PRICING_MODE_DIGITAL_PAPER_KEY,
  PublisherCampaignConfiguration,
  PublisherDefaultPricingModeByMediaType,
  PublisherDefaultPricingModeByMediaTypeKey,
} from 'components/common/types/Publisher.types';

type DirectSalesLinePricingModeInput = {
  pricingMode?: DirectSalesPricingMode;
  mediaType?: DirectSalesMediaType;
  mediaTypes?: DirectSalesMediaType[];
  networkCriteria?: unknown[];
};

const isDirectSalesMediaType = (key: string): key is DirectSalesMediaType =>
  (Object.values(DirectSalesMediaType) as string[]).includes(key);

const isPublisherDefaultPricingModeByMediaTypeKey = (key: string): key is PublisherDefaultPricingModeByMediaTypeKey =>
  key === PUBLISHER_DEFAULT_PRICING_MODE_DIGITAL_PAPER_KEY || isDirectSalesMediaType(key);

export const hasBothDigitalAndPaperMediaTypes = (mediaTypes: DirectSalesMediaType[]): boolean =>
  mediaTypes.includes(DirectSalesMediaType.DIGITAL) && mediaTypes.includes(DirectSalesMediaType.PAPER);

export const getPublisherDefaultPricingModeMediaTypeKey = (
  mediaType?: DirectSalesMediaType,
  mediaTypes?: DirectSalesMediaType[],
): PublisherDefaultPricingModeByMediaTypeKey | undefined => {
  const selectedMediaTypes = mediaTypes ?? (mediaType ? [mediaType] : []);

  if (hasBothDigitalAndPaperMediaTypes(selectedMediaTypes)) {
    return PUBLISHER_DEFAULT_PRICING_MODE_DIGITAL_PAPER_KEY;
  }

  if (mediaType) {
    return mediaType;
  }

  if (selectedMediaTypes.length === 1) {
    return selectedMediaTypes[0];
  }

  return undefined;
};

export const parseOptionalNonNetworkDirectSalesPricingMode = (
  raw: string | undefined,
): DirectSalesPricingMode.CPM | DirectSalesPricingMode.PANEL | undefined => {
  if (!raw || typeof raw !== 'string') return undefined;

  const normalized = raw.trim().toUpperCase();

  if (normalized === DirectSalesPricingMode.PANEL) return DirectSalesPricingMode.PANEL;
  if (normalized === DirectSalesPricingMode.CPM) return DirectSalesPricingMode.CPM;

  return undefined;
};

export const parseDefaultPricingModeByMediaType = (
  raw: string | Record<string, string> | undefined,
): PublisherDefaultPricingModeByMediaType | undefined => {
  if (raw === undefined) return undefined;

  let parsed: Record<string, unknown>;

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;

    try {
      parsed = JSON.parse(trimmed) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  } else {
    parsed = raw as Record<string, unknown>;
  }

  const result: PublisherDefaultPricingModeByMediaType = {};

  Object.entries(parsed).forEach(([key, value]) => {
    if (!isPublisherDefaultPricingModeByMediaTypeKey(key)) return;

    const mode = parseOptionalNonNetworkDirectSalesPricingMode(String(value));

    if (mode) {
      result[key] = mode;
    }
  });

  return Object.keys(result).length ? result : undefined;
};

export const resolvePublisherDefaultNonNetworkDirectSalesPricingMode = (
  campaignConfiguration: PublisherCampaignConfiguration,
  mediaType?: DirectSalesMediaType,
  mediaTypes?: DirectSalesMediaType[],
): DirectSalesPricingMode.CPM | DirectSalesPricingMode.PANEL => {
  const { defaultPricingModeByMediaType } = campaignConfiguration;
  const lookupKey = getPublisherDefaultPricingModeMediaTypeKey(mediaType, mediaTypes);

  if (lookupKey) {
    const mode = defaultPricingModeByMediaType?.[lookupKey];

    if (mode !== undefined) {
      return mode;
    }
  }

  return DirectSalesPricingMode.CPM;
};

export const resolveDirectSalesLinePricingMode = (
  line: DirectSalesLinePricingModeInput,
  campaignConfiguration: PublisherCampaignConfiguration,
): DirectSalesPricingMode => {
  if (line.networkCriteria && line.networkCriteria.length >= 1) {
    return DirectSalesPricingMode.NETWORK;
  }

  return (
    line.pricingMode ??
    resolvePublisherDefaultNonNetworkDirectSalesPricingMode(campaignConfiguration, line.mediaType, line.mediaTypes)
  );
};

export const resolveDirectSalesPricingModeForApi = (
  line: DirectSalesLinePricingModeInput,
  fallbackNonNetwork: DirectSalesPricingMode.CPM | DirectSalesPricingMode.PANEL = DirectSalesPricingMode.CPM,
): DirectSalesPricingMode => {
  if (line.networkCriteria && line.networkCriteria.length >= 1) {
    return DirectSalesPricingMode.NETWORK;
  }

  return line.pricingMode ?? fallbackNonNetwork;
};
